import React, { useState, useEffect } from 'react';
import { FaGlobe, FaPlus, FaCheck, FaTimes, FaSync, FaTrash, FaInfoCircle, FaCopy } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';

/**
 * Domain Settings component for schools
 * Allows schools to connect their own domains to their school portal
 */
const DomainSettings = () => {
  const { showToast } = useToast();
  
  // State for domains
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingDomain, setAddingDomain] = useState(false);
  const [verifyingDomain, setVerifyingDomain] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Fetch domains on component mount
  useEffect(() => {
    fetchDomains();
    
    // Use an abort controller to help with cleanup
    const controller = new AbortController();
    
    return () => {
      controller.abort();
    };
  }, []);
  
  // Fetch domains from API
  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await api.get('/schools/domains');
      
      // Handle the response data properly
      if (response.data && response.data.success) {
        setDomains(response.data.data || []);
      } else {
        setDomains([]);
        console.warn('Unexpected API response format:', response.data);
      }
    } catch (error) {
      console.error('Error fetching domains:', error);
      
      // Handle 404 errors more gracefully - likely means the API endpoint is not yet implemented
      if (error.response && error.response.status === 404) {
        showToast('Domain management is being set up. Please try again later.', 'info');
      } else {
        showToast('Failed to load domains. Please try again later.', 'error');
      }
      
      // Set domains to empty array on error
      setDomains([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle new domain input change
  const handleDomainChange = (e) => {
    setNewDomain(e.target.value.trim());
  };
  
  // Handle add new domain
  const handleAddDomain = async () => {
    if (!newDomain) {
      showToast('Please enter a domain name', 'error');
      return;
    }
    
    // Basic domain validation
    const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newDomain)) {
      showToast('Please enter a valid domain name', 'error');
      return;
    }
    
    try {
      setAddingDomain(true);
      
      const response = await api.post('/schools/domains', { domain: newDomain });
      setDomains([...domains, response.data.data]);
      setDnsRecords(response.data.dnsRecords || []);
      setSelectedDomain(response.data.data);
      
      showToast('Domain added successfully', 'success');
      setNewDomain('');
      setShowAddModal(false);
      setShowVerifyModal(true);
    } catch (error) {
      console.error('Error adding domain:', error);
      showToast(error.response?.data?.message || 'Failed to add domain', 'error');
    } finally {
      setAddingDomain(false);
    }
  };
  
  // Handle verify domain
  const handleVerifyDomain = async (domain) => {
    setSelectedDomain(domain);
    setVerifyingDomain(true);
    
    try {
      const response = await api.get(`/schools/domains/${domain._id}/verification`);
      setDnsRecords(response.data.dnsRecords || []);
      setShowVerifyModal(true);
    } catch (error) {
      console.error('Error getting verification details:', error);
      showToast('Failed to get verification details', 'error');
    } finally {
      setVerifyingDomain(false);
    }
  };
  
  // Handle check verification status
  const handleCheckVerification = async () => {
    if (!selectedDomain) return;
    
    try {
      setVerifyingDomain(true);
      
      const response = await api.post(`/schools/domains/${selectedDomain._id}/verify`);
      
      if (response.data.verified) {
        showToast('Domain verified successfully', 'success');
        fetchDomains();
        setShowVerifyModal(false);
      } else {
        showToast('Domain verification failed. Check your DNS records and try again.', 'error');
      }
    } catch (error) {
      console.error('Error verifying domain:', error);
      showToast(error.response?.data?.message || 'Failed to verify domain', 'error');
    } finally {
      setVerifyingDomain(false);
    }
  };
  
  // Copy DNS record to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('Copied to clipboard', 'success');
    });
  };
  
  // Handle delete domain
  const handleDeleteClick = (domain) => {
    setSelectedDomain(domain);
    setShowDeleteModal(true);
  };
  
  // Confirm delete domain
  const handleDeleteConfirm = async () => {
    if (!selectedDomain) return;
    
    try {
      await api.delete(`/schools/domains/${selectedDomain._id}`);
      
      showToast('Domain deleted successfully', 'success');
      setDomains(domains.filter(d => d._id !== selectedDomain._id));
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting domain:', error);
      showToast(error.response?.data?.message || 'Failed to delete domain', 'error');
    }
  };
  
  // Get domain status badge
  const getDomainStatusBadge = (status) => {
    switch (status) {
      case 'verified':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <FaCheck className="mr-1" /> Verified
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <FaSync className="mr-1" /> Pending Verification
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <FaTimes className="mr-1" /> Verification Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Unknown
          </span>
        );
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaGlobe className="mr-2 text-primary" /> Domain Settings
          </h1>
          <p className="text-gray-600 mt-1">
            Connect your school website domain to your Meira portal
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Add Domain
          </button>
        </div>
      </div>
      
      {/* Domain List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Your Domains</h2>
            <p className="text-sm text-gray-600 mt-1">
              Connect your custom domains to your school's shop and portal
            </p>
          </div>
          
          {domains.length === 0 ? (
            <div className="p-6 text-center">
              <FaGlobe className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No domains added yet</h3>
              <p className="text-gray-500 mb-4">
                Add your custom domain to provide a professional experience for your students and parents.
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-primary inline-flex items-center"
              >
                <FaPlus className="mr-2" /> Add Your First Domain
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Domain
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added On
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {domains.map((domain) => (
                    <tr key={domain._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {domain.name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getDomainStatusBadge(domain.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(domain.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {domain.status !== 'verified' && (
                          <button
                            onClick={() => handleVerifyDomain(domain)}
                            className="text-primary hover:text-primary-dark mx-2"
                          >
                            Verify
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteClick(domain)}
                          className="text-red-600 hover:text-red-900 mx-2"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Information Card */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-medium text-blue-800 mb-3 flex items-center">
          <FaInfoCircle className="mr-2" /> Using Custom Domains
        </h3>
        <div className="text-blue-700 space-y-2">
          <p>
            Custom domains help create a professional and branded experience for your students and parents.
          </p>
          <p>
            After adding a domain, you'll need to configure DNS records with your domain registrar to verify your ownership.
          </p>
          <p>
            Once verified, your domain will be connected to your school's shop and portal.
          </p>
        </div>
      </div>
      
      {/* Add Domain Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Domain"
      >
        <div className="p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Domain Name
            </label>
            <input
              type="text"
              value={newDomain}
              onChange={handleDomainChange}
              placeholder="example.com"
              className="form-input w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter your domain without 'http://' or 'www.'
            </p>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowAddModal(false)}
              className="btn-secondary"
              disabled={addingDomain}
            >
              Cancel
            </button>
            <button
              onClick={handleAddDomain}
              className="btn-primary"
              disabled={addingDomain}
            >
              {addingDomain ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Adding...
                </>
              ) : (
                <>Add Domain</>
              )}
            </button>
          </div>
        </div>
      </Modal>
      
      {/* Verify Domain Modal */}
      <Modal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        title="Verify Domain Ownership"
      >
        <div className="p-6">
          {selectedDomain && (
            <>
              <p className="mb-4">
                To verify your ownership of <strong>{selectedDomain.name}</strong>, you need to add the following DNS records:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium mb-2">DNS Records</h4>
                
                <div className="space-y-4">
                  {dnsRecords.map((record, index) => (
                    <div key={index} className="border border-gray-200 rounded p-3">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Record Type:</span>
                        <span className="text-sm">{record.type}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Host/Name:</span>
                        <div className="flex items-center">
                          <span className="text-sm">{record.name}</span>
                          <button 
                            onClick={() => copyToClipboard(record.name)}
                            className="ml-2 text-gray-400 hover:text-primary"
                          >
                            <FaCopy />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-700">Value/Points to:</span>
                        <div className="flex items-center">
                          <span className="text-sm">{record.value}</span>
                          <button 
                            onClick={() => copyToClipboard(record.value)}
                            className="ml-2 text-gray-400 hover:text-primary"
                          >
                            <FaCopy />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-6">
                After adding these records to your DNS configuration, click "Verify" to check if the domain has been properly configured.
                DNS changes can take up to 24-48 hours to propagate.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowVerifyModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
                <button
                  onClick={handleCheckVerification}
                  className="btn-primary"
                  disabled={verifyingDomain}
                >
                  {verifyingDomain ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Verifying...
                    </>
                  ) : (
                    <>Verify</>
                  )}
                </button>
              </div>
            </>
          )}
          {!selectedDomain && (
            <div className="text-center py-4">
              <p>No domain selected.</p>
            </div>
          )}
        </div>
      </Modal>
      
      {/* Delete Domain Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Domain"
      >
        <div className="p-6">
          <p className="mb-4">
            Are you sure you want to delete the domain <strong>{selectedDomain ? selectedDomain.name : ''}</strong>?
            This action cannot be undone.
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DomainSettings; 